import styled from 'styled-components';
import { Card, Typography, Box, Paper } from '@mui/material';

const BREAKPOINTS = {
  mobile: '768px'
};

// Layout Components
export const PageLayout = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f5f5;
  
  @media (max-width: ${BREAKPOINTS.mobile}) {
    flex-direction: column;
  }
`;

export const MainContent = styled.div`
  flex: 1;
  margin-left: 240px; /* 280px sidebar + 10px gap */
  
  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin-top: 60px;
    margin-left: 0;
  }
`;

export const ContentContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  height: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 1rem;
  }
`;

export const SectionContainer = styled.div`
  margin-bottom: 2rem;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin-bottom: 1.5rem;
  }
`;

// Typography Components
export const PageTitle = styled(Typography)`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 1.5rem;
  }
`;

export const PageSubtitle = styled(Typography)`
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 2rem;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
  }
`;

export const SectionTitle = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 1.25rem;
  }
`;

export const SectionSubtitle = styled(Typography)`
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 1rem;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 0.75rem;
  }
`;

// Form Components
export const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: ${BREAKPOINTS.mobile}) {
    flex-direction: column;
    width: 100%;
    
    > button {
      width: 100%;
    }
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin-bottom: 1rem;
  }
`;

export const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    flex-direction: column;
    margin-top: 1.5rem;

    > button {
      width: 100%;
    }
  }
`;

// Card Components
export const StyledCard = styled(Card)<{ $isSelected?: boolean }>`
  height: 100%;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  border: ${props => props.$isSelected ? '2px solid #1976d2' : '1px solid #1976d2'};
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    min-height: auto;
  }
`;

export const CardInner = styled.div`
  height: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 16px;
  }
`;

export const CardHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin-bottom: 16px;
  }
`;

export const CardTitle = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 8px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 1.25rem;
  }
`;

// Grid Components
export const GridContainer = styled.div<{
  alignItems?: string;
  justifyContent?: string;
  spacing?: number;
}>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.spacing ? `${props.spacing * 8}px` : '24px'};
  width: 100%;
  max-width: 1200px;
  margin: 32px auto 0;
  align-items: ${props => props.alignItems || 'stretch'};
  justify-content: ${props => props.justifyContent || 'start'};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 24px;
  }
`;

export const FlexContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

// List Components
export const ListContainer = styled.div`
  margin: 1rem 0;
`;

export const ListItem = styled.div`
  padding: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

// Stats Components
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

export const StatCard = styled(Paper)`
  padding: 1rem;
  text-align: center;
`;

export const StatValue = styled(Typography)`
  font-size: 2rem;
  font-weight: 700;
  color: #1976d2;
`;

export const StatLabel = styled(Typography)`
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
`;

// Table Components
export const TableContainer = styled.div`
  overflow-x: auto;
  margin: 1rem 0;
`;

export const ResponsiveTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    th, td {
      padding: 0.75rem;
    }
  }
`;

// Pricing Components
export const PriceContainer = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
`;

export const PriceAmount = styled(Typography)`
  font-size: 2.5rem;
  font-weight: 700;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 2rem;
  }
`;

export const PricePeriod = styled(Typography)`
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.6);
  margin-left: 4px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 0.875rem;
  }
`;

export const CardSubtext = styled(Typography)`
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 0.75rem;
  }
`;

export const FeaturesList = styled.div`
  flex-grow: 1;
  margin: 16px 0;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin: 12px 0;
    
    .MuiListItem-root {
      padding-top: 4px;
      padding-bottom: 4px;
    }
    
    .MuiListItemText-root {
      margin-top: 2px;
      margin-bottom: 2px;
    }
  }
`;

export const CheckboxContainer = styled(Box)`
  margin-top: 16px;
  margin-bottom: 16px;
`;

export const StrikethroughText = styled(Typography)`
  text-decoration: line-through;
  display: block;
  margin-bottom: 8px;
  color: #d32f2f;
`;

export const PromotionalText = styled(Typography)`
  color: #d32f2f;
  margin-bottom: 8px;
`;

// Alert and Message Components
export const AlertContainer = styled.div`
  margin: 1rem 0;
`;

export const MessageBox = styled.div`
  padding: 1rem;
  border-radius: 4px;
  background-color: ${props => props.color || '#f5f5f5'};
  margin: 1rem 0;
`;

// Loading Components
export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

// Empty State Components
export const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: rgba(0, 0, 0, 0.6);
`;

// Modal Components
export const ModalContent = styled.div`
  padding: 2rem;
  
  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 1rem;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  
  @media (max-width: ${BREAKPOINTS.mobile}) {
    flex-direction: column;
    
    > button {
      width: 100%;
    }
  }
`;
